import { Component, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { TableModule, TableLazyLoadEvent } from "primeng/table";
import { MultiSelectModule } from 'primeng/multiselect';
import { DropdownModule } from 'primeng/dropdown';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';

import { CampaignService } from '../../../services/campaign.service';
import { CreateCampaign, Cluster, EmailTemplate, EmailTemplateVariable, WhatsAppTemplate, WhatsAppTemplateVariables } from '../../../models/campaign';

@Component({
  selector: 'app-campaign-form',
  standalone: true,
  imports: [ReactiveFormsModule, TableModule, MultiSelectModule, CommonModule, ToastModule, DropdownModule],
  providers: [MessageService],
  templateUrl: './campaign-form.component.html',
  styleUrl: './campaign-form.component.scss'
})
export class CampaignFormComponent {
  readonly title = 'Campanhas de Marketing';
  readonly subtitle = 'Crie e gerencie as campanhas para enviar notificações aos usuários.';

  errorMessage: string | null = null;

  campaignForm: FormGroup;
  isCreatingCampaign: boolean = false;
  createUserError: string = '';
  channelOptions = [
    { label: 'WhatsApp', value: 'WHATSAPP' },
    { label: 'Push', value: 'PUSH' },
    { label: 'E-mail', value: 'EMAIL' }
  ];
  @Output() campaignCreated = new EventEmitter<void>();

  isFetchingClusters: boolean = false;
  clusters: Cluster[] = []
  totalClusters: number = 0;
  clusterPage: number = 1;
  rowsPerPage = 10;
  isClusterChosen: boolean = false;

  isFetchingEmailTemplates: boolean = false;
  emailTemplates: EmailTemplate[] = [];
  emailTemplateOptions: { label: string, value: string }[] = [];
  isFetchingEmailTemplateVariables: boolean = false;
  emailVariables: EmailTemplateVariable[] = [];

  isFetchingWhatsAppTemplates: boolean = false;
  whatsappTemplates: WhatsAppTemplate[] = [];
  whatsappTemplateOptions: { label: string, value: string }[] = [];
  whatsappTemplateIdByName: Record<string, string> = {};
  isFetchingWhatsAppVariables: boolean = false;
  whatsappVariables: { component: string, placeholder: string }[] = [];


  constructor(
    private fb: FormBuilder,
    private campaignService: CampaignService,
    private messageService: MessageService
  ) {
    this.campaignForm = this.fb.group({
      cluster: [null, Validators.required],
      title: ['', Validators.required],
      channels: [[]],
      push_settings: this.fb.group({
        message: [''],
        image_url: ['']
      }),
      email_settings: this.fb.group({
        email_template: [''],
        email_template_variables: this.fb.group({})
      }),
      whatsapp_settings: this.fb.group({
        whatsapp_template: [''],
        whatsapp_template_variables: this.fb.group({})
      })
    });
  }

  ngOnInit(): void {
    this.isClusterChosen = false;
    this.fetchClusters();
    this.fetchEmailTemplates();
    this.fetchWhatsAppTemplates();
  }

  fetchClusters() {
    this.isFetchingClusters = true;

    this.campaignService.listClusters(undefined, this.clusterPage).subscribe({
      next: (response) => {
        this.clusters = response.results;
        this.totalClusters = response.count;
        this.isFetchingClusters = false;
      },
      error: (error) => {
        this.errorMessage = 'Erro ao carregar clusters.';
        console.error('Error fetching clusters:', error);
        this.isFetchingClusters = false;
      }
    })
  }

  selectCluster(cluster: Cluster): void {
    this.campaignForm.patchValue({
      cluster: cluster
    });

    this.campaignForm.get('cluster')?.updateValueAndValidity();
    console.log(this.campaignForm)
  }

  fetchEmailTemplates() {
    this.isFetchingEmailTemplates = true;

    this.campaignService.listEmailTemplates().subscribe({
      next: (response) => {
        this.emailTemplates = response.data;
        this.isFetchingEmailTemplates = false;

        this.emailTemplateOptions = this.emailTemplates.map(template => ({
          label: template.name,
          value: template.alias
        }));
        console.log(this.emailTemplateOptions)
      },
      error: (error) => {
        this.errorMessage = 'Erro ao carregar templates de e-mail.';
        console.error('Error fetching email templates:', error);
        this.isFetchingEmailTemplates = false;
      }
    })
  }

  fetchEmailTemplateVariables(templateId: string) {
    this.isFetchingEmailTemplateVariables = true;
    this.emailVariables = [];

    this.campaignService.listEmailTemplateVariables(templateId).subscribe({
      next: (response) => {
        this.emailVariables = response;
        this.isFetchingEmailTemplateVariables = false;

        const group = this.campaignForm.get('email_settings.email_template_variables') as FormGroup;
        Object.keys(group.controls).forEach(k => group.removeControl(k));
        response.forEach(v => group.addControl(v.key, this.fb.control(v.fallback_value)));
      },
      error: (error) => {
        this.errorMessage = 'Erro ao carregar variáveis do template.';
        console.error('Error fetching email template variables:', error);
        this.isFetchingEmailTemplateVariables = false;
      }
    });
  }

  onTemplateChange(alias: string) {
    const template = this.emailTemplates.find(t => t.alias === alias);
    if (template) this.fetchEmailTemplateVariables(template.id);
  }

  fetchWhatsAppTemplates() {
    this.isFetchingWhatsAppTemplates = true;
    this.campaignService.listWhatsAppTemplates().subscribe({
      next: (response) => {
        this.whatsappTemplates = response.data;
        this.whatsappTemplateOptions = response.data.map(t => ({
          label: `${t.name} (${t.language})`,
          value: t.name  // Meta API usa o name, não o ID
        }));
        this.whatsappTemplateIdByName = Object.fromEntries(
          response.data.map(t => [t.name, t.id])
        );
        this.isFetchingWhatsAppTemplates = false;
      },
      error: () => {
        this.isFetchingWhatsAppTemplates = false;
      }
    });
  }

  onWhatsAppTemplateChange(templateName: string) {
    const templateId = this.whatsappTemplateIdByName[templateName];
    if (!templateId) return;

    this.isFetchingWhatsAppVariables = true;
    this.whatsappVariables = [];

    const group = this.campaignForm.get('whatsapp_settings.whatsapp_template_variables') as FormGroup;
    Object.keys(group.controls).forEach(k => group.removeControl(k));

    this.campaignService.getWhatsAppTemplateVariables(templateId).subscribe({
      next: (variables: WhatsAppTemplateVariables) => {
        this.whatsappVariables = Object.entries(variables).flatMap(([component, placeholders]) =>
          placeholders.map(placeholder => ({ component, placeholder }))
        );
        this.whatsappVariables.forEach(v => {
          const base = this.getWhatsAppControlName(v);
          group.addControl(`${base}_dynamic`, this.fb.control(v.placeholder));
          group.addControl(`${base}_fallback`, this.fb.control(''));
        });
        this.isFetchingWhatsAppVariables = false;
      },
      error: () => {
        this.isFetchingWhatsAppVariables = false;
      }
    });
  }

  onLazyLoad(event: TableLazyLoadEvent) {
    const first = event.first ?? 0;
    this.clusterPage = Math.floor(first / this.rowsPerPage) + 1;
    this.fetchClusters();
  }

  onSubmit() {
    if (this.campaignForm.valid) {
      this.isCreatingCampaign = true;

      const formData = this.campaignForm.value;

      const rawVars = formData.whatsapp_settings?.whatsapp_template_variables || {};
      const whatsappVars: Record<string, [string, string]> = {};
      this.whatsappVariables.forEach(v => {
        const base = this.getWhatsAppControlName(v);
        whatsappVars[base] = [rawVars[`${base}_dynamic`], rawVars[`${base}_fallback`]];
      });

      const payload: CreateCampaign = {
        cluster: formData.cluster?.id,
        title: formData.title,
        message: formData.push_settings?.message || '',
        channels: formData.channels,
        image_url: formData.push_settings?.image_url,
        email_template: formData.email_settings?.email_template,
        email_template_variables: formData.email_settings?.email_template_variables,
        whatsapp_template: formData.whatsapp_settings?.whatsapp_template,
        whatsapp_template_variables: this.whatsappVariables.length ? whatsappVars : undefined,
      };

      this.campaignService.createCampaign(payload).subscribe({
        next: (response) => {
          this.isCreatingCampaign = false;
          this.campaignCreated.emit();
        },
        error: (error) => {
          this.isCreatingCampaign = false;
          this.createUserError = error?.error?.detail || 'Não foi possível criar a campanha.';
        },
      })
    }
  }

  hasChannel(channel: string): boolean {
    const selectedChannels = this.campaignForm.get('channels')?.value || [];
    return selectedChannels.includes(channel);
  }

  get visibleEmailVariables() {
    return this.emailVariables.filter(v => v.key !== 'unsubscribe_url');
  }

  getWhatsAppControlName(variable: { component: string, placeholder: string }): string {
    return `${variable.component}_${variable.placeholder.replace(/\{|\}/g, '')}`;
  }

}
